import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  UsageRule,
  DOSE_UNITS,
  DOSE_DENOMINATORS,
  PHI_V2_UNITS,
  DoseUnit,
  DoseDenominator,
  PhiV2Unit,
} from '../../services/sprayService';
import { Field } from '../patterns';
import type { CropTypeNode } from '../../services/cropTypeService';
import type { PestRef } from '../../services/pestService';
import CropMultiSelect from './CropMultiSelect';
import PestMultiSelect from './PestMultiSelect';

interface Props {
  value: UsageRule[];
  onChange: (rules: UsageRule[]) => void;
  cropOptions?: CropTypeNode[];
  pestOptions?: PestRef[];
}

const blankRule = (): UsageRule => ({ crops: [], pests: [] });

export const UsageRulesEditor: React.FC<Props> = ({
  value,
  onChange,
  cropOptions = [],
  pestOptions = [],
}) => {
  const { t } = useTranslation();
  const rules: UsageRule[] = value.length > 0 ? value : [blankRule()];

  const update = (idx: number, patch: Partial<UsageRule>) => {
    onChange(rules.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const updateDose = (idx: number, patch: Partial<NonNullable<UsageRule['dose']>>) => {
    const current = rules[idx].dose ?? { value: 0, unit: '' as DoseUnit, denominator: '100L' as DoseDenominator };
    update(idx, { dose: { ...current, ...patch } });
  };

  const updatePhi = (idx: number, patch: Partial<NonNullable<UsageRule['phi']>>) => {
    const current = rules[idx].phi ?? { value: 0, unit: 'days' as PhiV2Unit };
    update(idx, { phi: { ...current, ...patch } });
  };

  const remove = (idx: number) => onChange(rules.filter((_, i) => i !== idx));
  const add = () => onChange([...rules, blankRule()]);

  return (
    <div data-testid="usage-rules-editor">
      {rules.map((rule, i) => (
        <div
          key={i}
          data-testid={`usage-rule-row-${i}`}
          className="pat-field-grid pat-field-grid--3 align-items-end mb-3"
        >
          <Field
            label={t('spray.products.usageRules.cropsLabel', { defaultValue: 'Crops' })}
            controlId={`usage-rule-row-${i}-crops`}
          >
            <CropMultiSelect options={cropOptions} value={rule.crops || []} onChange={crops => update(i, { crops })} />
          </Field>
          <Field
            label={t('spray.products.usageRules.pestsLabel', { defaultValue: 'Pests (none = any)' })}
            controlId={`usage-rule-row-${i}-pests`}
          >
            <PestMultiSelect options={pestOptions} value={rule.pests || []} onChange={pests => update(i, { pests })} />
          </Field>
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              aria-label={t('spray.products.usageRules.remove', { defaultValue: 'Remove rule' })}
              data-testid={`usage-rule-row-${i}-remove`}
              onClick={() => remove(i)}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Dose row */}
          <Field
            label={t('spray.products.usageRules.doseValue', { defaultValue: 'Dose value' })}
            controlId={`usage-rule-row-${i}-dose-value`}
          >
            <input
              id={`usage-rule-row-${i}-dose-value`}
              data-testid={`usage-rule-row-${i}-dose-value`}
              type="number"
              className="pat-input"
              value={Number.isFinite(rule.dose?.value) && rule.dose?.value !== 0 ? String(rule.dose?.value) : ''}
              onChange={e => updateDose(i, { value: parseFloat(e.target.value) || 0 })}
            />
          </Field>
          <Field
            label={t('spray.products.usageRules.doseUnit', { defaultValue: 'Unit' })}
            controlId={`usage-rule-row-${i}-dose-unit`}
          >
            <select
              id={`usage-rule-row-${i}-dose-unit`}
              data-testid={`usage-rule-row-${i}-dose-unit`}
              className="pat-input"
              value={rule.dose?.unit || ''}
              onChange={e => updateDose(i, { unit: e.target.value as DoseUnit })}
            >
              <option value="">—</option>
              {DOSE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field
            label={t('spray.products.usageRules.doseDenominator', { defaultValue: 'Per' })}
            controlId={`usage-rule-row-${i}-dose-denominator`}
          >
            <select
              id={`usage-rule-row-${i}-dose-denominator`}
              data-testid={`usage-rule-row-${i}-dose-denominator`}
              className="pat-input"
              value={rule.dose?.denominator || ''}
              onChange={e => updateDose(i, { denominator: e.target.value as DoseDenominator })}
            >
              <option value="">—</option>
              {DOSE_DENOMINATORS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>

          {/* PHI row */}
          <Field
            label={t('spray.products.usageRules.phiValue', { defaultValue: 'PHI value' })}
            controlId={`usage-rule-row-${i}-phi-value`}
          >
            <input
              id={`usage-rule-row-${i}-phi-value`}
              data-testid={`usage-rule-row-${i}-phi-value`}
              type="number"
              className="pat-input"
              value={Number.isFinite(rule.phi?.value) && rule.phi?.value !== 0 ? String(rule.phi?.value) : ''}
              onChange={e => updatePhi(i, { value: parseFloat(e.target.value) || 0 })}
            />
          </Field>
          <Field
            label={t('spray.products.usageRules.phiUnit', { defaultValue: 'PHI unit' })}
            controlId={`usage-rule-row-${i}-phi-unit`}
          >
            <select
              id={`usage-rule-row-${i}-phi-unit`}
              data-testid={`usage-rule-row-${i}-phi-unit`}
              className="pat-input"
              value={rule.phi?.unit || ''}
              onChange={e => updatePhi(i, { unit: e.target.value as PhiV2Unit })}
            >
              <option value="">—</option>
              {PHI_V2_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>

          <div style={{ gridColumn: '1 / span 3' }}>
            <Field
              label={t('spray.products.usageRules.notes', { defaultValue: 'Notes' })}
              controlId={`usage-rule-row-${i}-notes`}
            >
              <input
                id={`usage-rule-row-${i}-notes`}
                data-testid={`usage-rule-row-${i}-notes`}
                type="text"
                className="pat-input"
                value={rule.notes || ''}
                onChange={e => update(i, { notes: e.target.value })}
              />
            </Field>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="pat-btn pat-btn--subtle"
        data-testid="usage-rule-add"
        onClick={add}
      >
        <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.products.usageRules.add', { defaultValue: 'Add usage rule' })}
      </button>
    </div>
  );
};

export default UsageRulesEditor;
